/** @jsxImportSource @emotion/react */
import React from "react";
import { Profile } from "./elements/Profile";
import { NavbarButtons } from "./elements/NavbarButtons";
import { NavbarContainer } from "./elements/NavbarContainer";
import { NavbarLinks } from "./elements/NavbarLinks";
import styled from "@emotion/styled";
import {
  ActivityColoredIcon,
  ActivityIcon,
  BebopBWhite,
  BebopWhiteIcon,
  Cloud,
  Note,
  TokenWrapColoredIcon,
  TokenWrapIcon,
  TradeColoredIcon,
  TradeIcon,
} from "components/Icons";
import { BEBOP_ROUTES, NAV_LINKS } from "config/consts";
import { Theme } from "styles/theme";
import { Notice } from "./elements/Notice";
import { useMediaQuery } from "usehooks-ts";
import { Link } from "react-router-dom";
import { useDisplayOnClick } from "hooks/useDisplayOnClick";
import { NavbarWrapper } from "./elements/NavbarWrapper";

export const NavbarV2Desktop = () => {
  const matchesMinHeight = useMediaQuery("(min-height: 507px)");
  const {
    isDisplayed: isProfileActionsDisplayed,
    onClick: onProfileClick,
    elementToClickRef: profileButtonRef,
    elementToDisplayRef: profileActionsRef,
    forceClose: closeProfileActions,
  } = useDisplayOnClick();

  return (
    <NavbarWrapper>
      <NavbarContainer>
        <NavbarContent>
          <Profile
            isDisplayed={isProfileActionsDisplayed}
            elementToClickRef={profileButtonRef as React.Ref<HTMLDivElement>}
            elementToDisplayRef={profileActionsRef as React.Ref<HTMLDivElement>}
            closeDisplay={closeProfileActions}
            onClick={onProfileClick}
          />

          <div>
            <NavbarButtons />
            <LineBreak />
            <NavbarLinks closeProfileActions={closeProfileActions} />
          </div>
        </NavbarContent>

        <BottomContainer>
          {matchesMinHeight && (
            <NoticeContainer>
              <Notice
                title="API Docs"
                body="Integrate with Bebop. Tools for Nerds!"
                link={NAV_LINKS.DOCUMENTATION_API_INTRODUCTION}
              />
            </NoticeContainer>
          )}
          <Link to="/">
            <BebopWhiteIcon
              width={118}
              height={40}
              style={{ minWidth: "118px", minHeight: "40px" }}
            />
          </Link>
        </BottomContainer>
      </NavbarContainer>
    </NavbarWrapper>
  );
};

// Sub-components

const NavbarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 88px;
`;

const LineBreak = styled.hr`
  margin-top: 13px;
  margin-bottom: 13px;
`;

const BottomContainer = styled.div`
  margin-top: 30px;
`;

const NoticeContainer = styled.div`
  min-height: 88px;
  margin-bottom: 33px;
`;

// Helpers

export interface NavbarButtonOptions {
  symbol: {
    default: React.FC<React.SVGProps<SVGSVGElement>>;
    active: React.FC<React.SVGProps<SVGSVGElement>>;
  };
  text: string;
  colors?: { hover?: string; active?: string; selected?: string };
  pathName?: string;
}

export const getNavbarButtonOptions = (theme: Theme): Array<NavbarButtonOptions> => [
  {
    symbol: { default: TradeIcon, active: TradeColoredIcon },
    text: "Trade",
    pathName: BEBOP_ROUTES.Trade,
    colors: {
      hover: theme["Colour ramps"].Green[600],
      active: theme["Colour ramps"].Green[500],
      selected: theme["Colour ramps"].Green[600],
    },
  },
  {
    symbol: { default: TokenWrapIcon, active: TokenWrapColoredIcon },
    text: "Token wrap",
    pathName: BEBOP_ROUTES.Wrap,
    colors: {
      hover: theme["Colour ramps"].Pink[400],
      active: theme["Colour ramps"].Pink[300],
      selected: theme["Colour ramps"].Pink[400],
    },
  },
  {
    symbol: { default: ActivityIcon, active: ActivityColoredIcon },
    text: "Activity",
    pathName: BEBOP_ROUTES.Activity,
    colors: {
      hover: theme["Colour ramps"].Yellow[400],
      active: theme["Colour ramps"].Yellow[300],
      selected: theme["Colour ramps"].Yellow[500],
    },
  },
];

export interface NavbarLinkItemOptions {
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  text: string;
  to: string;
}

export const linkItems: Array<NavbarLinkItemOptions> = [
  {
    text: "FAQs",
    icon: Note,
    to: NAV_LINKS.FAQ,
  },
  {
    text: "API Docs",
    icon: Cloud,
    to: NAV_LINKS.DOCUMENTATION_API_INTRODUCTION,
  },
  {
    text: "About",
    icon: BebopBWhite,
    to: "/",
  },
];
